import { observer } from 'mobx-react-lite'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { uniq } from 'lodash'
import { track, useTracking } from 'lib/babylistHealthAnalytics'
import { Controller, useForm } from 'react-hook-form'
import Checkbox from 'baby-ui/foundation/CheckBox'
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  Link,
  Select,
  Typography,
  useTheme,
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import LoadingButton from 'baby-ui/compounds/LoadingButton'
import TermsAndConditions from 'baby-ui/compounds/TermsAndConditions'
import { ReplacementPartsFormSchema } from './validationSchema'
import useStyles from './styles'
import { STORE_NAME } from '../../constants'

export interface ReplacementPartsFormProps {
  formErrorMessage: string
  heroProducts: APIResponse.HeroProduct[]
  loading: boolean
  onError: (error: APIResponse.DME.SubscriptionErrorType) => void
  onSubmit: (params: SubmittedFormValues) => void
}

interface SubmittedFormValues {
  productId: number
  aobAcknowledged: boolean
}

export interface ReplacementPartsFormValues {
  productId?: APIResponse.HeroProduct['id']
  aobAcknowledged: boolean
}

// If there is a single brand name gleaned from the available products, use it.
// If there are multiple brands, or no brands, show no brand name. This should be
// an edge case as parts typically have the same brand as the pump.
const getBrandName = (heroProducts: APIResponse.HeroProduct[]) => {
  const brandNames = uniq(heroProducts.map((heroProduct) => heroProduct.brand))
  return brandNames.length === 1 ? brandNames[0] : ''
}

const ReplacementPartsForm = observer(
  ({
    formErrorMessage,
    heroProducts,
    loading,
    onSubmit,
  }: ReplacementPartsFormProps) => {
    const classes = useStyles()
    const theme = useTheme()
    const tracker = useTracking()

    const productOptions = heroProducts
      .map(
        (heroProduct) => heroProduct.products || heroProduct.availableProducts
      )
      .flat()
    const brandName = getBrandName(heroProducts)

    const { control, errors, handleSubmit, setValue, watch } =
      useForm<ReplacementPartsFormValues>({
        defaultValues: {
          aobAcknowledged: false,
          productId: undefined,
        },
        validationContext: { productOptions },
        validationSchema: ReplacementPartsFormSchema,
      })
    const [showAOBDialog, setShowAOBDialog] = useState<boolean>(false)

    const watchProductId = watch('productId')
    const watchAobAcknowledged = watch('aobAcknowledged')

    const onAgreeToAOB = () => {
      setValue('aobAcknowledged', true)
      setShowAOBDialog(false)
    }
    const onDisagreeToAOB = () => {
      setValue('aobAcknowledged', false)
      setShowAOBDialog(false)
    }

    const submitHandler = () => {
      if (watchProductId === undefined) return

      onSubmit({
        productId: watchProductId,
        aobAcknowledged: watchAobAcknowledged,
      })
    }

    const handleProductIdChange = (
      event: React.ChangeEvent<HTMLSelectElement>
    ) => {
      const newValue = Number(event.target.value)
      setValue('productId', newValue, true)
    }

    useEffect(() => {
      tracker.trackEvent({
        event: track.replacementPartsOrderFormViewed,
        storeName: STORE_NAME,
      })
    }, [])

    return (
      <Box
        bgcolor={theme.palette.background.contrast}
        flexGrow={1}
        px={{ xs: 4, sm: 0 }}
        py={8}
      >
        <Box className={classes.formContainer}>
          <TermsAndConditions
            clauses={[
              {
                title: (
                  <p>
                    By digitally signing this document you acknowledge that you
                    are still using your breast pump and are in need of
                    replacement parts to keep your breast pump functioning to
                    the best of its capabilities.
                  </p>
                ),
                type: 'unordered',
                terms: [],
              },
              {
                title: (
                  <p>
                    You also authorize Babylist Health LLC and Medequip Inc., a
                    member of Babylist's Preferred Durable Medical Equipment
                    Network, to submit claims to your insurance company on your
                    behalf and request on your behalf all insurance information,
                    prescriptions and benefits for products provided as well as
                    to release your medical records to any person, organization,
                    company and/or agency which may be involved in providing
                    your care.
                  </p>
                ),
                type: 'unordered',
                terms: [],
              },
              {
                title: (
                  <p>
                    You also acknowledge that you have not ordered breast pump
                    replacement parts through another DME provider.
                  </p>
                ),
                type: 'unordered',
                terms: [],
              },
            ]}
            open={showAOBDialog}
            title="Replacement Parts Use Acknowledgement"
            onAgree={onAgreeToAOB}
            onDisagree={onDisagreeToAOB}
          />

          <Typography
            className={classes.formTitle}
            color="primary"
            component="h1"
            variant="h4"
          >
            You're eligible for free replacement parts!
          </Typography>
          <Typography
            className={classes.formSubtitle}
            color="primary"
            variant="body2"
          >
            You'll receive FREE {brandName} replacement parts through your
            insurance and we'll even cover the shipping.
          </Typography>

          <form onSubmit={handleSubmit(submitHandler)}>
            {formErrorMessage && (
              <Alert severity="error" variant="standard">
                <span>{formErrorMessage}</span>
              </Alert>
            )}
            <div className={classes.selectInput}>
              <FormControl
                fullWidth
                error={!!errors.productId}
                variant="outlined"
              >
                <InputLabel htmlFor="productId">
                  Select your flange size:{' '}
                </InputLabel>
                <Controller
                  aria-required
                  required
                  as={(props) => (
                    <Select {...props} native onChange={handleProductIdChange}>
                      <option aria-label="None" />
                      {productOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.attributes?.size}
                        </option>
                      ))}
                    </Select>
                  )}
                  control={control}
                  id="productId"
                  label="Select your flange size"
                  name="productId"
                />
                {errors.productId && (
                  <FormHelperText>{errors.productId.message}</FormHelperText>
                )}
              </FormControl>
            </div>

            <Typography
              className={classes.partsText}
              component="div"
              variant="body2"
            >
              If you are still using your pump, your insurance can cover the
              following items:
              <ul>
                <li>2 sets of breast flanges</li>
                <li>2 sets of tubing</li>
                <li>2 backflow protectors</li>
                <li>2 duckbill valves</li>
                <li>2 wide neck milk bottles with cap, disc, and lid</li>
              </ul>
            </Typography>

            <FormControl fullWidth className={classes.checkbox}>
              <Controller
                required
                as={Checkbox}
                control={control}
                label={
                  <Typography variant="caption">
                    By checking this box, I confirm that I am still pumping and
                    require breast pump replacement parts. I also agree to the{' '}
                    {/* component=button means this is rendering a button, not an anchor */}
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      variant="inherit"
                      onClick={() => {
                        setShowAOBDialog(true)
                      }}
                    >
                      Assignment of Benefits
                    </Link>
                    .
                  </Typography>
                }
                name="aobAcknowledged"
              />
              {errors.aobAcknowledged && (
                <FormHelperText>
                  {errors.aobAcknowledged.message}
                </FormHelperText>
              )}
            </FormControl>
            <LoadingButton
              fullWidth
              color="secondary"
              disabled={
                !watchProductId ||
                !watchAobAcknowledged ||
                loading ||
                !!errors.productId
              }
              loading={loading}
              type="submit"
              variant="contained"
            >
              Continue
            </LoadingButton>
          </form>
        </Box>
      </Box>
    )
  }
)

export default ReplacementPartsForm
